import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';
import CreateMatchModal from './CreateMatchModal';
import MatchRegistrationsList from './MatchRegistrationsList';
import DeclareResultsModal from './DeclareResultsModal';
import RoomDetailsModal from './RoomDetailsModal';
import ManualPaymentsList from './ManualPaymentsList';
import SendNotification from './SendNotification';
import PaymentMethodsManager from './PaymentMethodsManager';
import EditMatchModal from './EditMatchModal';

function AdminDashboard({ session, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalMatches: 0,
    activeMatches: 0,
    totalUsers: 0,
    totalRevenue: 0,
  });
  const [matches, setMatches] = useState([]);
  const [showCreateMatch, setShowCreateMatch] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showRegistrations, setShowRegistrations] = useState(false);
  const [showDeclareResults, setShowDeclareResults] = useState(false);
  const [showRoomDetails, setShowRoomDetails] = useState(false);
  const [showEditMatch, setShowEditMatch] = useState(false);

  useEffect(() => {
    fetchAdminStats();
    fetchMatches();
  }, [session]); // Re-run if session changes to ensure token availability

  const fetchAdminStats = async () => {
    try {
      const token = localStorage.getItem('admin_token') || session?.access_token;

      if (!token) return;

      // Fetch matches
      const matchesResponse = await fetch(`${API_BASE}/matches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (matchesResponse.ok) {
        const matchesData = await matchesResponse.json();
        setStats({
          totalMatches: matchesData.length,
          activeMatches: matchesData.filter(m => m.status !== 'completed' && m.status !== 'cancelled').length,
          totalUsers: 0, // Would need a separate endpoint
          totalRevenue: 0, // Would need calculation
        });
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem('admin_token') || session?.access_token;

      if (!token) return;

      const response = await fetch(`${API_BASE}/matches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const handleMatchCreated = () => {
    fetchMatches();
    fetchAdminStats();
    setShowCreateMatch(false);
    toast.success('Match created successfully! 🎮');
  };

  const handleViewRegistrations = (match) => {
    setSelectedMatch(match);
    setShowRegistrations(true);
  };

  const handleDeclareResults = (match) => {
    setSelectedMatch(match);
    setShowDeclareResults(true);
  };

  const handleResultsDeclared = () => {
    setShowDeclareResults(false);
    setSelectedMatch(null);
    toast.success('Results declared successfully! 🏆');
    fetchMatches();
  };

  const handleEditRoom = (match) => {
    setSelectedMatch(match);
    setShowRoomDetails(true);
  };

  const handleRoomUpdated = () => {
    setShowRoomDetails(false);
    fetchMatches();
  };

  const handleEditMatch = (match) => {
    setSelectedMatch(match);
    setShowEditMatch(true);
  };

  const handleEditSuccess = () => {
    setShowEditMatch(false);
    setSelectedMatch(null);
    fetchMatches();
    toast.success('Match updated successfully! ✨');
  };

  const updateMatchStatus = async (matchId, newStatus) => {
    try {
      if (!window.confirm(`Are you sure you want to mark match as ${newStatus}?`)) return;

      const token = localStorage.getItem('admin_token') || session?.access_token;

      const response = await fetch(`${API_BASE}/admin/matches/${matchId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success(`Match status updated to ${newStatus}`);
        fetchMatches();
      } else {
        const err = await response.json();
        toast.error(err.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Update status error:', error);
      toast.error('Failed to update status');
    }
  };

  const renderOverview = () => (
    <div>
      <h2 style={{
        fontSize: '2rem',
        marginBottom: '30px',
        fontFamily: 'Orbitron, sans-serif',
        color: 'var(--primary-color)'
      }}>
        📊 Overview
      </h2>

      <div className="grid grid-4" style={{ marginBottom: '30px' }}>
        <div className="stat-card">
          <div className="stat-icon">🎮</div>
          <div className="stat-label">Total Matches</div>
          <div className="stat-value">{stats.totalMatches}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⚡</div>
          <div className="stat-label">Active Matches</div>
          <div className="stat-value">{stats.activeMatches}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{stats.totalUsers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-label">Total Revenue</div>
          <div className="stat-value">₹{(stats.totalRevenue / 100).toFixed(0)}</div>
        </div>
      </div>

      <div style={{
        background: 'var(--secondary-bg)',
        padding: '30px',
        borderRadius: '12px',
        border: '1px solid var(--border-color)'
      }}>
        <h3 style={{ fontSize: '1.5rem', marginBottom: '20px' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => setShowCreateMatch(true)}>
            ➕ Create Match
          </button>
          <button className="btn btn-secondary" onClick={() => setActiveTab('matches')}>
            📋 View All Matches
          </button>
          <button className="btn btn-outline">
            👥 Manage Users
          </button>
        </div>
      </div>
    </div>
  );

  const renderMatches = () => (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <h2 style={{
          fontSize: '2rem',
          fontFamily: 'Orbitron, sans-serif',
          color: 'var(--primary-color)'
        }}>
          🎮 Manage Matches
        </h2>
        <button className="btn btn-primary" onClick={() => setShowCreateMatch(true)}>
          ➕ Create Match
        </button>
      </div>

      {matches.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'var(--secondary-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)'
        }}>
          <p style={{ fontSize: '4rem', marginBottom: '20px' }}>🎯</p>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>No matches created yet</h3>
          <button className="btn btn-primary" onClick={() => setShowCreateMatch(true)} style={{ marginTop: '20px' }}>
            Create Your First Match
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Mode</th>
                <th>Map</th>
                <th>Slots</th>
                <th>Entry Fee</th>
                <th>Start Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((match) => (
                <tr key={match.id}>
                  <td><strong>{match.title}</strong></td>
                  <td style={{ textTransform: 'uppercase' }}>{match.mode}</td>
                  <td>{match.map}</td>
                  <td>
                    {/* Show Available / Total */}
                    {match.slot_count - (match.registrations_count || 0)} / {match.slot_count}
                  </td>
                  <td>₹{match.entry_fee}</td>
                  <td>
                    <div>{new Date(match.start_ts).toLocaleDateString()}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {new Date(match.start_ts).toLocaleTimeString()}
                    </div>
                  </td>
                  <td>
                    <select
                      className={`badge ${(match.status === 'completed') ? 'badge-success' :
                        (match.status === 'ongoing') ? 'badge-warning' :
                          (match.status === 'cancelled') ? 'badge-error' :
                            'badge-info'
                        }`}
                      style={{
                        background: 'transparent',
                        border: '1px solid currentColor',
                        cursor: 'pointer',
                        color: 'inherit',
                        padding: '0 5px',
                        height: 'auto',
                        outline: 'none'
                      }}
                      value={match.status || 'scheduled'}
                      onChange={(e) => updateMatchStatus(match.id, e.target.value)}
                    >
                      <option value="scheduled" style={{ color: 'black' }}>📅 Scheduled</option>
                      <option value="ongoing" style={{ color: 'black' }}>▶️ Ongoing</option>
                      <option value="completed" style={{ color: 'black' }}>🏁 Completed</option>
                      <option value="cancelled" style={{ color: 'black' }}>❌ Cancelled</option>
                    </select>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                        onClick={() => handleViewRegistrations(match)}
                      >
                        👥 View
                      </button>

                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                        onClick={() => handleEditRoom(match)}
                      >
                        🏠 Room
                      </button>

                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                        onClick={() => handleEditMatch(match)}
                      >
                        ✏️ Edit
                      </button>

                      {(match.status === 'ongoing' || match.status === 'completed') && (
                        <button
                          className="btn btn-success"
                          style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                          onClick={() => handleDeclareResults(match)}
                        >
                          🏆 Results
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      }
    </div >
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'matches':
        return renderMatches();
      case 'payments':
        return <ManualPaymentsList session={session} />;
      case 'notifications':
        return <SendNotification session={session} />;
      case 'methods':
        return <PaymentMethodsManager session={session} />;
      default:
        return renderOverview();
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{
          fontSize: '2.5rem',
          marginBottom: '10px',
          fontFamily: 'Orbitron, sans-serif'
        }}>
          Admin Dashboard 👑
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>
          Manage tournaments, users, and results
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '30px',
        borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
        paddingBottom: '15px'
      }}>
        <button
          className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
          style={{
            background: activeTab === 'overview' ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
            border: activeTab === 'overview' ? '1px solid rgba(0, 240, 255, 0.5)' : '1px solid transparent',
            padding: '10px 20px',
            borderRadius: '8px'
          }}
        >
          📊 Overview
        </button>
        <button
          className={`nav-link ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
          style={{
            background: activeTab === 'matches' ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
            border: activeTab === 'matches' ? '1px solid rgba(0, 240, 255, 0.5)' : '1px solid transparent',
            padding: '10px 20px',
            borderRadius: '8px'
          }}
        >
          🎮 Matches
        </button>
        <button
          className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
          style={{
            background: activeTab === 'payments' ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
            border: activeTab === 'payments' ? '1px solid rgba(0, 240, 255, 0.5)' : '1px solid transparent',
            padding: '10px 20px',
            borderRadius: '8px'
          }}
        >
          💰 Manual Payments
        </button>
        <button
          className={`nav-link ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
          style={{
            background: activeTab === 'notifications' ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
            border: activeTab === 'notifications' ? '1px solid rgba(0, 240, 255, 0.5)' : '1px solid transparent',
            padding: '10px 20px',
            borderRadius: '8px'
          }}
        >
          🔔 Notifications
        </button>
        <button
          className={`nav-link ${activeTab === 'methods' ? 'active' : ''}`}
          onClick={() => setActiveTab('methods')}
          style={{
            background: activeTab === 'methods' ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
            border: activeTab === 'methods' ? '1px solid rgba(0, 240, 255, 0.5)' : '1px solid transparent',
            padding: '10px 20px',
            borderRadius: '8px'
          }}
        >
          💳 Payment Methods
        </button>
      </div>

      {/* Content */}
      {renderContent()}

      {/* Modals */}
      {showCreateMatch && (
        <CreateMatchModal
          session={session}
          onClose={() => setShowCreateMatch(false)}
          onSuccess={handleMatchCreated}
        />
      )}

      {showRegistrations && selectedMatch && (
        <MatchRegistrationsList
          match={selectedMatch}
          session={session}
          onClose={() => {
            setShowRegistrations(false);
            setSelectedMatch(null);
          }}
        />
      )}

      {showDeclareResults && selectedMatch && (
        <DeclareResultsModal
          match={selectedMatch}
          session={session}
          onClose={() => {
            setShowDeclareResults(false);
            setSelectedMatch(null);
          }}
          onSuccess={handleResultsDeclared}
        />
      )}

      {showRoomDetails && selectedMatch && (
        <RoomDetailsModal
          match={selectedMatch}
          session={session}
          onClose={() => {
            setShowRoomDetails(false);
            setSelectedMatch(null);
          }}
          onSuccess={handleRoomUpdated}
        />
      )}

      {showEditMatch && selectedMatch && (
        <EditMatchModal
          match={selectedMatch}
          session={session}
          onClose={() => {
            setShowEditMatch(false);
            setSelectedMatch(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
}

export default AdminDashboard;